import { useEffect, useRef } from 'react';
import {
  type IssuedDeviceCredentialDto,
  type LocationPingBatchEntryDto,
  type LocationPingBatchResultDto,
} from '@hrms/shared';
import { post } from '@/lib/api-client';
import { env } from '@/lib/env';
import { useAuth } from '@/features/auth/use-auth';
import { useReportingState } from './location-api';
import { backgroundGeolocation, isNativeApp, type NativePosition } from './native-bridge';
import { clearQueue, dropSent, enqueue, peekBatch } from './device-queue';

/**
 * How often to try the queue while reporting, regardless of new positions.
 *
 * A minute is far below the server's 48-hour window and cheap when the queue is
 * empty — the check is a localStorage read that returns early.
 */
const DRAIN_INTERVAL_MS = 60_000;

/**
 * Reporting from the background, in the Android shell.
 *
 * A browser stops the moment its page is hidden, which is exactly when a
 * technician is riding between client sites. The shell runs a foreground
 * service that keeps delivering positions with the phone locked, and this is
 * what receives them.
 *
 * **What the free plugin does not do**, and what this therefore must. It hands
 * each position to JavaScript and keeps nothing itself — no native queue, no
 * native upload. So the WebView has to be alive to receive a position at all,
 * and if this code loses one it is gone. The foreground service keeps the app's
 * process alive, which is what makes that workable, but it is a weaker
 * guarantee than a plugin that posts natively: a WebView Android decides to
 * tear down takes the reporting with it until something brings the app back.
 * Gaps from that are indistinguishable from gaps from a flat battery, which is
 * why the terms say gaps are normal and are not held against anybody.
 *
 * Runs alongside the foreground reporter rather than replacing it. In a browser
 * this does nothing at all.
 */
export function useBackgroundReporter(): void {
  const { user } = useAuth();
  const { data: state } = useReportingState();

  // SessionUser carries `id`, not `userId`. Same field the foreground reporter
  // keys its own storage on, so the two agree about who this is.
  const userId = user?.id ?? null;
  const shouldReport = state?.shouldReport ?? false;
  const enabled = state?.enabled ?? false;

  // The watcher id, so a change of shift can stop the one that is running
  // rather than stacking a second on top of it.
  const watcherId = useRef<string | null>(null);

  // Cleared on teardown so a stopped shift does not leave a timer uploading.
  const drain = useRef<number | null>(null);

  useEffect(() => {
    if (!isNativeApp() || !userId) return;

    const plugin = backgroundGeolocation();
    if (!plugin) return;

    let cancelled = false;

    const stop = async () => {
      const id = watcherId.current;
      watcherId.current = null;
      if (id) await plugin.removeWatcher({ id }).catch(() => undefined);
    };

    if (!enabled || !shouldReport) {
      void stop();
      return;
    }

    // Already watching. Starting again would run two foreground services and
    // report everything twice.
    if (watcherId.current) return;

    const start = async () => {
      const token = await deviceToken(userId);
      if (!token || cancelled) return;

      const id = await plugin.addWatcher(
        {
          // Android shows this permanently while the service runs. Employees
          // are told about it in the terms, and it is not removable.
          backgroundTitle: 'KioBytes HR is recording your location',
          backgroundMessage: 'Recording while you are clocked in. Stops when you clock out.',
          requestPermissions: true,
          // A stale fix is worth having: the first one arrives immediately
          // rather than after the phone gets a lock, and its own timestamp says
          // how old it is.
          stale: true,
          // Metres. Coarse enough not to report a phone sitting on a desk,
          // fine enough to show a technician moving between jobs.
          distanceFilter: 50,
        },
        (position, error) => {
          if (error || !position) return;
          void handle(userId, token, position);
        },
      );

      if (cancelled) {
        await plugin.removeWatcher({ id }).catch(() => undefined);
        return;
      }

      watcherId.current = id;

      /*
        Drain on start, and then on a timer.

        Uploading only when a new position arrives leaves the last positions of
        a shift stranded: the watcher stops at clock-out, nothing arrives to
        trigger an upload, and the tail sits in storage until the next shift —
        by which time the server's 48-hour window may have refused it. It also
        strands the whole backlog of a phone that came back into signal while
        sitting still, because standing still is exactly what stops the plugin
        producing positions.
      */
      void flush(userId, token);
      drain.current = window.setInterval(() => void flush(userId, token), DRAIN_INTERVAL_MS);
    };

    void start();

    return () => {
      cancelled = true;
      if (drain.current !== null) {
        window.clearInterval(drain.current);
        drain.current = null;
      }
      void stop();
    };
  }, [userId, enabled, shouldReport]);
}

/** Queues a position, then tries to send whatever is waiting. */
async function handle(userId: string, token: string, position: NativePosition): Promise<void> {
  const at = new Date(position.time ?? Date.now()).toISOString();

  const entry: LocationPingBatchEntryDto = {
    // The device's clock, because nothing else knows when this was taken. The
    // server clamps it and marks the stored row as device-timed.
    recordedAt: at,
    latitude: position.latitude,
    longitude: position.longitude,
    // `== null` rather than `=== null`: the plugin's type says number|null but
    // an absent field arrives as undefined, and Math.round(undefined) is NaN,
    // which JSON.stringify turns into null anyway — silently, and only for the
    // field that says how much to trust the position.
    accuracyMeters: position.accuracy == null ? null : Math.round(position.accuracy),
    deviceReportedAt: at,
    batteryPercent: null,
  };

  enqueue(userId, entry);
  await flush(userId, token);
}

/*
  One upload at a time, per user.

  `handle` is called straight from the plugin callback, which fires per position
  and does not wait. Overlapping uploads read the same head of the queue, send it
  twice, and then each removes what it thinks it sent — so a position that only a
  failing upload ever carried gets deleted, silently and permanently. Chaining
  them makes the read-send-remove sequence atomic with respect to itself.

  It also restores the batching this endpoint was built for: while one upload is
  in flight the positions behind it accumulate, so the next request carries the
  backlog instead of issuing one HTTP request per position.
*/
const uploads = new Map<string, Promise<void>>();

function flush(userId: string, token: string): Promise<void> {
  const next = (uploads.get(userId) ?? Promise.resolve())
    .then(() => sendQueue(userId, token))
    .catch(() => undefined);

  uploads.set(userId, next);
  return next;
}

/**
 * Sends the queue, oldest first.
 *
 * The queue is cleared on a 200 whatever the server did with the contents. It
 * answers 200 even when it stored nothing — a position taken after clocking out
 * is refused for good — so treating "accepted" as the test would leave those in
 * the queue forever and it would never drain. Anything worth retrying is a
 * non-200, and then nothing is dropped.
 */
async function sendQueue(userId: string, token: string): Promise<void> {
  const queued = peekBatch(userId);
  if (queued.length === 0) return;

  let response: Response;

  try {
    // Deliberately not the shared axios client: that attaches the session's
    // bearer token, and the whole point of a device credential is to work when
    // there is no live session to attach.
    response = await fetch(`${env.VITE_API_BASE_URL}/location/devices/pings/batch`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ pings: queued.map((entry) => entry.ping) }),
    });
  } catch {
    // Offline. The queue is the answer to this, so leaving it intact is the
    // whole behaviour rather than an error path.
    return;
  }

  /*
    A rejected credential is permanent, and treating it as a network blip wedges
    the handset forever: the dead token stays in storage, every upload 401s, and
    the queue grows until its oldest entries age past the server's window. This
    is reachable in normal use — revoking a phone from the My Phones screen does
    exactly this — and the revoke dialog promises the phone re-registers next
    time somebody clocks in. Forgetting the token is what makes that true.
  */
  if (response.status === 401 || response.status === 403) {
    forgetToken(userId);
    return;
  }

  if (!response.ok) return;

  dropSent(
    userId,
    queued.map((entry) => entry.qid),
  );

  const result = (await response.json().catch(() => null)) as LocationPingBatchResultDto | null;

  // The company switched tracking off. Nothing still queued will ever be
  // accepted, so holding it only grows a queue that can never drain — and the
  // credential is forgotten so the next start asks the server again rather
  // than reporting into a wall.
  if (result && !result.trackingEnabled) {
    clearQueue(userId);
    forgetToken(userId);
  }
}

/* ------------------------------------------------------------------------ */

function tokenKey(userId: string): string {
  return `kiobytes-hr:device-token:${userId}`;
}

/**
 * This phone's credential, registering one the first time.
 *
 * Registration needs a live session, which is why it happens here — in the
 * foreground, when somebody has just opened the app — rather than at the moment
 * a background position needs sending, when there may be no session at all.
 */
async function deviceToken(userId: string): Promise<string | null> {
  try {
    const existing = window.localStorage.getItem(tokenKey(userId));
    if (existing) return existing;

    const issued = await post<IssuedDeviceCredentialDto>('/location/devices', {
      label: deviceLabel(),
    });

    window.localStorage.setItem(tokenKey(userId), issued.token);
    return issued.token;
  } catch {
    // No session, no permission, or offline. Reporting simply does not start;
    // the next time the app opens it tries again.
    return null;
  }
}

function forgetToken(userId: string): void {
  try {
    window.localStorage.removeItem(tokenKey(userId));
  } catch {
    // Nothing to do.
  }
}

/**
 * What this phone is called in the revoke list.
 *
 * Taken from the user agent, which on Android carries the model. It only has to
 * be good enough for somebody to recognise which handset to un-register.
 */
function deviceLabel(): string {
  const match = /Android[^;]*;\s*([^)]+?)\s*(?:Build|\))/.exec(navigator.userAgent);
  const model = match?.[1]?.trim();
  return model && model.length > 0 ? model.slice(0, 60) : 'Android phone';
}
